import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SavedCallsTab from "@/components/SavedCallsTab";
import CandidaturesList from "@/components/CandidaturesList";

/**
 * Base de conocimiento de ayudas y financiación para cine y audiovisual.
 * Información extraída de convocatorias oficiales (ICAA, BOE) y manuales sectoriales
 * (Spain Film Commission, ICEC, Junta de Andalucía...).
 * Última actualización: 2026-05-26
 */
const SECTIONS = [
  { id: "intro", label: "1. Panorama general" },
  { id: "icaa-selectivas", label: "2. ICAA · Ayudas selectivas" },
  { id: "icaa-generales", label: "3. ICAA · Ayudas generales" },
  { id: "fiscal-general", label: "4. Incentivo fiscal · Régimen general" },
  { id: "fiscal-canarias", label: "5. Incentivo fiscal · Canarias" },
  { id: "fiscal-navarra", label: "6. Incentivo fiscal · Navarra" },
  { id: "fiscal-pais-vasco", label: "7. Incentivo fiscal · País Vasco" },
  { id: "ccaa", label: "8. Ayudas autonómicas (CCAA)" },
  { id: "ue", label: "9. Ayudas europeas e internacionales" },
  { id: "compatibilidad", label: "10. Compatibilidad y topes" },
  { id: "calendario", label: "11. Calendario de convocatorias" },
  { id: "activas", label: "12. Convocatorias activas" },
  { id: "presentar", label: "13. Cómo presentar el dossier" },
  { id: "enlaces", label: "14. Enlaces útiles" },
];

export default function FundingPage() {
  const [active, setActive] = useState("intro");
  const [tab, setTab] = useState("info");   // "info" | "calendar"

  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    const onScroll = () => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top > 80 && r.top < 240) { setActive(id); return; }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gb-bg font-serif text-stone-900" data-testid="funding-page">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-12">
        <div className="mb-8 border-b border-stone-300/70 pb-8 sm:mb-12 sm:pb-12">
          <p className="text-[10px] font-semibold uppercase tracking-[0.45em] text-[#26775E]">grabarte · base de conocimiento</p>
          <h1 className="mt-3 font-display text-3xl leading-[1.05] tracking-tight sm:text-5xl">Ayudas y financiación del audiovisual</h1>
          <p className="mt-3 max-w-3xl text-sm text-stone-600 sm:text-base">
            Resumen práctico de las principales vías de financiación pública para proyectos
            cinematográficos en España: ayudas del ICAA, incentivos fiscales por territorio y
            convocatorias autonómicas y europeas.
          </p>
          <p className="mt-2 text-xs text-stone-500">Última actualización: 26 de mayo de 2026 · Verifica siempre la convocatoria oficial vigente.</p>

          <div className="mt-6 inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.25em]" data-testid="funding-tabs">
            <button onClick={() => setTab("info")} data-testid="funding-tab-info"
                    className={`rounded-full px-4 py-1.5 transition ${tab === "info" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:text-stone-900"}`}>
              Información
            </button>
            <button onClick={() => setTab("calendar")} data-testid="funding-tab-calendar"
                    className={`rounded-full px-4 py-1.5 transition ${tab === "calendar" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:text-stone-900"}`}>
              Convocatorias
            </button>
            <button onClick={() => setTab("candidatures")} data-testid="funding-tab-candidatures"
                    className={`rounded-full px-4 py-1.5 transition ${tab === "candidatures" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:text-stone-900"}`}>
              Mis candidaturas
            </button>
          </div>
        </div>

        {tab === "candidatures" ? (
          <CandidaturesList />
        ) : tab === "calendar" ? (
          <SavedCallsTab />
        ) : (
        <div className="grid gap-8 lg:grid-cols-[260px,1fr] lg:gap-12">
          <aside className="hidden lg:block">
            <nav className="sticky top-24 space-y-1 text-sm" data-testid="funding-toc">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => goto(s.id)} data-testid={`toc-${s.id}`}
                        className={`block w-full rounded px-3 py-1.5 text-left transition ${
                          active === s.id ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:bg-stone-200/60 hover:text-stone-900"
                        }`}>
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>

          <div className="lg:hidden">
            <select value={active} onChange={(e) => goto(e.target.value)} data-testid="funding-toc-mobile"
                    className="w-full rounded-md border border-stone-300 bg-white p-3 text-sm">
              {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <article className="max-w-none">
            <Section id="intro" title="1. Panorama general">
              <p>
                En España, la financiación de un proyecto audiovisual se construye habitualmente
                combinando varias fuentes:
              </p>
              <ul>
                <li><strong>Ayudas del ICAA</strong> (estatales): selectivas y generales.</li>
                <li><strong>Incentivos fiscales</strong> a la producción cinematográfica (Impuesto de Sociedades).</li>
                <li><strong>Ayudas autonómicas</strong> (Cataluña/ICEC, Andalucía, Galicia/AGADIC, Madrid, etc.).</li>
                <li><strong>Coproducciones con TV</strong> (RTVE, TV3, ETB, À Punt, CRTVG…).</li>
                <li><strong>Programas europeos</strong> (Creative Europe MEDIA, Eurimages, Ibermedia).</li>
                <li><strong>Aportación de la productora</strong> y financiación privada.</li>
              </ul>
              <Tip>
                El conjunto de ayudas públicas suele tener un tope global del <strong>50% del coste</strong> del
                proyecto (con excepciones reforzadas hasta el 75–85% para documentales, animación,
                dirección novel, lengua cooficial, etc.).
              </Tip>
            </Section>

            <Section id="icaa-selectivas" title="2. ICAA · Ayudas selectivas">
              <p>
                Convocadas anualmente por el Instituto de la Cinematografía y de las Artes
                Audiovisuales para proyectos en desarrollo / pre-producción. Se valoran sobre
                memoria + presupuesto, sin requerir que la película esté rodada.
              </p>

              <h3>2.1 Líneas y dotación 2026</h3>
              <ul>
                <li><strong>Especial valor cinematográfico, cultural o social.</strong></li>
                <li><strong>Documentales.</strong></li>
                <li><strong>Nuevos realizadores</strong> (máx. 2 películas previas).</li>
                <li><strong>Obras experimentales.</strong></li>
                <li><strong>Coproducciones extranjeras minoritarias.</strong></li>
              </ul>
              <p>
                Dotación 2026: <strong>20 M€</strong> de partida, ampliable hasta <strong>37 M€</strong>.
                Plazo de solicitud 2026: <strong>2–20 de marzo de 2026</strong> (revisa anualmente en BOE).
              </p>

              <h3>2.2 Topes económicos</h3>
              <ul>
                <li><strong>Tope general por ayuda</strong>: 800.000 € por proyecto.</li>
                <li><strong>Animación</strong>: hasta 1.000.000 €.</li>
                <li><strong>Coproducciones internacionales (minoritaria)</strong>: hasta 300.000 €.</li>
                <li><strong>Tope sobre presupuesto</strong>: ICAA cubre como máx. el 50% del presupuesto (75% si tiene <em>especial valor cultural</em>).</li>
                <li><strong>Experimentales</strong>: presupuesto entre 80.000 € y 300.000 €.</li>
              </ul>

              <h3>2.3 Topes % de ayuda pública total (RD 1929/2020)</h3>
              <Table cols={["Supuesto", "% máx. ayuda pública"]} rows={[
                ["Régimen general", "50%"],
                ["Coproducciones internacionales", "60%"],
                ["Mujer directora / documental / especial valor artístico (hasta 2,5 M€)", "75%"],
                ["Discapacidad ≥ 33%", "80%"],
                ["Dirección novel (≤2 películas) o lengua cooficial (no español) — pelis ≤1,5 M€", "85%"],
              ]} />

              <h3>2.4 Requisitos clave</h3>
              <ul>
                <li>Nacionalidad española del proyecto (test cultural ICAA).</li>
                <li>No haber solicitado calificación por edad antes del fallo.</li>
                <li>≥ <strong>50% del gasto</strong> en España o en autores/equipos españoles.</li>
                <li>Incluir <strong>subtitulado especial + audiodescripción</strong> (accesibilidad).</li>
                <li>Plan de <strong>sostenibilidad medioambiental</strong> y/o medición de huella de carbono.</li>
              </ul>

              <h3>2.5 Reserva de cuota</h3>
              <ul>
                <li>Mínimo <strong>40%</strong> del crédito para proyectos dirigidos exclusivamente por mujeres.</li>
                <li><strong>15–25%</strong> para documentales.</li>
                <li>Mínimo <strong>10%</strong> para animación.</li>
                <li>Mínimo <strong>5%</strong> para experimentales.</li>
                <li>Mínimo <strong>5%</strong> para coproducciones minoritarias.</li>
              </ul>

              <h3>2.6 Documentación requerida</h3>
              <ol>
                <li>Guion (y/o escaleta o argumento) y sinopsis.</li>
                <li>Memoria de máx. 30 páginas A4: memoria de dirección (intenciones + bases creativas) y memoria de producción (motivaciones, público, plan de promoción/difusión nacional e internacional).</li>
                <li>Presupuesto en modelo oficial ICAA (12 capítulos).</li>
                <li>Plan de trabajo: tiempo de rodaje + localizaciones.</li>
                <li>Ficha técnico-artística en formulario oficial.</li>
                <li>Historial de la productora solicitante, dirección y guion.</li>
                <li>Para coproducción extranjera: resolución de aprobación de la coproducción (salvo aprobación previa por ICAA).</li>
                <li>Para agrupaciones de empresas: documento oficial firmado con productora-gestora, % de titularidad y compromisos de ejecución.</li>
              </ol>

              <h3>2.7 Criterios de valoración (largometrajes generales)</h3>
              <p><strong>Puntuación objetiva (máx. 50 pts)</strong></p>
              <Table cols={["Apartado", "Puntos"]} rows={[
                ["Carácter cultural (test ICAA)", "hasta 3"],
                ["Trayectoria del director/a", "hasta 5"],
                ["Solvencia de la empresa solicitante o gestora", "hasta 8"],
                ["Viabilidad económica y financiera", "hasta 10"],
                ["Impacto socioeconómico, inversión e innovación", "hasta 24"],
                ["Bonus: domicilio social Islas Canarias", "+2 (tope total 50)"],
              ]} />
              <p><strong>Puntuación subjetiva (máx. 50 pts)</strong></p>
              <Table cols={["Apartado", "Puntos"]} rows={[
                ["Calidad / valor artístico / nivel de desarrollo (subtotal 45)", "—"],
                ["i. Relevancia y originalidad (+1 si teaser/videopitch)", "10"],
                ["ii. Investigación que desarrolla", "5"],
                ["iii. Estructura", "5"],
                ["iv. Propuesta formal", "5"],
                ["v. Memoria de dirección", "5"],
                ["vi. Memoria de producción", "5"],
                ["vii. Nivel de desarrollo", "5"],
                ["viii. Equipo autoral y técnico", "5"],
                ["Adecuación del presupuesto al proyecto", "5"],
              ]} />

              <h3>2.8 Criterios de valoración (obras experimentales)</h3>
              <p><strong>Puntuación objetiva (hasta 26 pts)</strong></p>
              <ul>
                <li>Financiación del proyecto: 10 pts.</li>
                <li>Solvencia de la empresa: 8 pts.</li>
                <li>Participación de mujeres: 8 pts.</li>
              </ul>
              <p><strong>Puntuación objetiva valorativa (hasta 74 pts)</strong></p>
              <ul>
                <li>Calidad y valor artístico (hasta 70 pts): relevancia (10), memoria dirección (10), memoria producción (5), recursos expresivos novedosos (10), estructura (5), propuesta formal (5), tratamiento visual (5), tratamiento sonoro (5), nivel de desarrollo/madurez, equipo autoral/técnico (10).</li>
                <li>Adecuación del presupuesto: hasta 4 pts.</li>
              </ul>

              <Tip>
                Texto regulador básico: <strong>Orden ECD/2784/2015</strong>, de 18 de diciembre — reconocimiento
                del coste de la película y de la inversión del productor. Revisa el BOE de tu año.
              </Tip>
            </Section>

            <Section id="icaa-generales" title="3. ICAA · Ayudas generales">
              <p>
                Son ayudas <strong>a posteriori</strong>: la película ya está terminada / estrenada y se reparte
                un fondo en función del impacto (espectadores, premios, festivales, criterios culturales y de
                diversidad). No requieren proyecto ex-ante.
              </p>
              <ul>
                <li>Convocadas anualmente por ICAA.</li>
                <li>Pueden combinarse con las selectivas si la peli las recibió previamente.</li>
                <li>Beneficiario: la productora titular de los derechos.</li>
                <li>Importe: variable según fórmula automática (cuota por espectador + factores de calidad).</li>
              </ul>
            </Section>

            <Section id="fiscal-general" title="4. Incentivo fiscal · Régimen general España">
              <h3>4.1 Producción española (Art. 36.1 LIS)</h3>
              <ul>
                <li><strong>30%</strong> sobre el primer millón de base.</li>
                <li><strong>25%</strong> sobre el exceso.</li>
                <li>Tope: <strong>20 M€</strong> por producción · <strong>10 M€</strong> por episodio (series).</li>
                <li>Aplicable también a productores e <strong>inversores financieros</strong> mediante AIE.</li>
              </ul>

              <h3>4.2 Producción extranjera ejecutada en España (Art. 36.2 LIS)</h3>
              <ul>
                <li>Misma escala: <strong>30% / 25%</strong>.</li>
                <li>Gasto mínimo en España: <strong>1 M€</strong> (200.000 € en animación o VFX).</li>
                <li>Tope: <strong>20 M€</strong> por producción.</li>
                <li>La productora ejecutora española actúa como brazo local.</li>
              </ul>

              <h3>4.3 Compatibilidad</h3>
              <p>
                El conjunto (deducción + subvenciones públicas) no puede superar el <strong>50% del coste</strong>
                de producción. Excepciones que elevan el tope al 60–85%:
              </p>
              <ul>
                <li>Cortometrajes.</li>
                <li>Obras en lengua cooficial distinta del castellano.</li>
                <li>Animación.</li>
                <li>Documentales.</li>
                <li>Producciones dirigidas exclusivamente por mujeres.</li>
                <li>Coproducciones internacionales con territorios de bajo nivel de producción.</li>
              </ul>
            </Section>

            <Section id="fiscal-canarias" title="5. Incentivo fiscal · Canarias (REF)">
              <p>
                Régimen Económico y Fiscal de Canarias — el más potente del Estado para audiovisual.
              </p>
              <h3>5.1 Producción española</h3>
              <ul>
                <li><strong>54%</strong> sobre el primer millón de base.</li>
                <li><strong>45%</strong> sobre el exceso.</li>
                <li>Tope: <strong>36 M€</strong> por producción · <strong>18 M€</strong> por episodio.</li>
              </ul>
              <h3>5.2 Producción extranjera ejecutada en Canarias</h3>
              <ul>
                <li>Mismas escalas: <strong>54% / 45%</strong>.</li>
                <li>Gasto mínimo en Canarias: <strong>200.000 €</strong> (animación) o <strong>1 M€</strong> (resto).</li>
                <li>Tope: <strong>36 M€</strong> por producción.</li>
              </ul>
              <h3>5.3 Instrumentos complementarios</h3>
              <ul>
                <li><strong>ZEC</strong> (Zona Especial Canaria): IS reducido al 4% para empresas radicadas.</li>
                <li><strong>RIC</strong> (Reserva para Inversiones en Canarias): hasta 90% de deducción de la BI no distribuida si se reinvierte.</li>
                <li>Bonus +2 puntos en valoración objetiva de ayudas selectivas ICAA si domicilio social en Canarias.</li>
              </ul>
            </Section>

            <Section id="fiscal-navarra" title="6. Incentivo fiscal · Navarra (régimen foral)">
              <h3>6.1 Producción española</h3>
              <ul>
                <li>Deducción general: <strong>35%</strong>.</li>
                <li>Supuestos reforzados: <strong>40%</strong> (mujer directora, lengua cooficial, animación, documental, dirección novel, etc.).</li>
                <li>Tope: <strong>5 M€</strong> por producción.</li>
              </ul>
              <h3>6.2 Producción extranjera</h3>
              <ul>
                <li><strong>35%</strong> sobre el gasto realizado en Navarra.</li>
                <li>Gasto mínimo en Navarra: <strong>200.000 €</strong>.</li>
                <li>Tope: <strong>5 M€</strong>.</li>
              </ul>
              <Tip>
                Navarra exige sustancia: que el rodaje, la postproducción o el equipo se ejecuten realmente
                allí. La hacienda foral examina con detalle la trazabilidad.
              </Tip>
            </Section>

            <Section id="fiscal-pais-vasco" title="7. Incentivo fiscal · País Vasco (Diputaciones forales)">
              <p>
                Cada Diputación foral (Bizkaia, Gipuzkoa, Álava) tiene su propia normativa del IS. En general:
              </p>
              <ul>
                <li>Deducciones similares al régimen general (30% / 25%) con matices según territorio.</li>
                <li>Bizkaia y Gipuzkoa tienen líneas específicas para producción audiovisual en euskera con tipos reforzados.</li>
                <li>Posible compatibilidad con deducciones I+D+i si el proyecto incluye innovación tecnológica (animación, VFX).</li>
              </ul>
              <Tip>
                Consulta directamente la Diputación foral del territorio del rodaje. Las cifras varían cada
                ejercicio y a veces se reforman por norma foral específica.
              </Tip>
            </Section>

            <Section id="ccaa" title="8. Ayudas autonómicas (CCAA)">
              <h3>8.1 Cataluña · ICEC</h3>
              <ul>
                <li><strong>Desarrollo</strong>: 1,5 M€ en 2025. Cubre hasta el 60% del presupuesto.</li>
                <li>Topes: 25.000 € / 36.000 € / 50.000 € según formato (largo, serie, documental).</li>
                <li>Líneas adicionales: producción en catalán/aranés, exhibición, distribución.</li>
              </ul>
              <h3>8.2 Andalucía · AAIICC / Junta</h3>
              <ul>
                <li><strong>Desarrollo</strong> (productoras independientes y AIE): convocatoria anual diciembre-enero.</li>
                <li><strong>Producción</strong>: tres líneas (nuevos realizadores, largometrajes/TV-movies, documentales).</li>
                <li><strong>Salas de exhibición</strong>: 1,47 M€ en 2025.</li>
              </ul>
              <h3>8.3 Galicia · AGADIC</h3>
              <ul>
                <li>Producción audiovisual y cinematográfica de iniciativa autonómica.</li>
                <li>Refuerzo si gallego o coproducción con TVG.</li>
              </ul>
              <h3>8.4 Comunidad de Madrid</h3>
              <ul>
                <li>Subvenciones a producción audiovisual y a series de animación.</li>
                <li>Madrid Film Office: facilita rodajes y conexión con incentivos fiscales del régimen común.</li>
              </ul>
              <h3>8.5 Comunidad Valenciana · IVC</h3>
              <ul>
                <li>Ayudas a guion, desarrollo, producción y postproducción.</li>
                <li>Refuerzo si valenciano o coproducción con À Punt.</li>
              </ul>
              <h3>8.6 Otras CCAA con líneas activas</h3>
              <ul>
                <li>Aragón, Asturias, Baleares (IB Film), Castilla-La Mancha, Castilla y León, Extremadura, Murcia, La Rioja, Cantabria.</li>
                <li>Las cifras y plazos varían anualmente; consulta la web del organismo audiovisual autonómico.</li>
              </ul>
              <Tip>
                Estrategia clásica: combinar una ayuda autonómica de <strong>desarrollo</strong> (mientras el proyecto
                está en escritura) con una <strong>selectiva ICAA</strong> en pre-producción y, ya rodando,
                aplicar el <strong>incentivo fiscal</strong> del territorio.
              </Tip>
            </Section>

            <Section id="ue" title="9. Ayudas europeas e internacionales">
              <h3>9.1 Creative Europe MEDIA</h3>
              <ul>
                <li><strong>Development</strong> (Single Project / Slate): 25.000–60.000 € por proyecto.</li>
                <li><strong>Co-development</strong>: hasta 60.000 €.</li>
                <li><strong>TV and Online Content</strong>: hasta 500.000 € para series y documentales con potencial paneuropeo.</li>
                <li>Plataformas de mercado, distribución, alfabetización mediática.</li>
              </ul>

              <h3>9.2 Eurimages (Consejo de Europa)</h3>
              <ul>
                <li>Coproducciones europeas de largo y documental.</li>
                <li>Aportación media: 150.000–500.000 €.</li>
                <li>España aporta cuota anual; pueden solicitar coproducciones con al menos 2 países miembros.</li>
              </ul>

              <h3>9.3 Ibermedia</h3>
              <ul>
                <li>Programa iberoamericano. Líneas: desarrollo, coproducción, formación, distribución.</li>
                <li>Para coproducciones España–Latinoamérica + Portugal.</li>
              </ul>

              <h3>9.4 EU Documentary Network / IDFA Bertha / Sundance</h3>
              <p>
                Fondos privados internacionales para documental de autor. Se aplican
                por inglés con dossier y trailer.
              </p>
            </Section>

            <Section id="compatibilidad" title="10. Compatibilidad y topes globales">
              <p>
                Las ayudas se acumulan con reglas:
              </p>
              <ul>
                <li>Tope global de <strong>50%</strong> del coste reconocido por ICAA (suma de todas las ayudas públicas).</li>
                <li>Excepciones que elevan al <strong>60% / 75% / 85%</strong>: documental, animación, lengua cooficial, dirección novel, mujer directora, discapacidad.</li>
                <li>El <strong>incentivo fiscal</strong> y las <strong>subvenciones</strong> son compatibles dentro del tope.</li>
                <li>Una misma ayuda no es compatible consigo misma (no se puede solicitar 2 veces).</li>
                <li>Las ayudas autonómicas pueden tener sus propios topes adicionales.</li>
              </ul>
              <Tip>
                <strong>Regla práctica</strong>: en el plan de financiación, suma todas las ayudas públicas (ICAA +
                CCAA + deducción fiscal estimada) y verifica que no superan el porcentaje aplicable a tu caso.
                Si lo superas, alguna se reduce automáticamente al recortar la última en ser concedida.
              </Tip>
            </Section>

            <Section id="calendario" title="11. Calendario tipo de convocatorias">
              <Table cols={["Convocatoria", "Plazo aproximado", "Cuándo conviene preparar"]} rows={[
                ["ICAA Selectivas", "Marzo (anual)", "Octubre–febrero año anterior"],
                ["ICAA Generales", "Junio-julio (anual)", "Tras estreno y antes de cierre fiscal"],
                ["Creative Europe MEDIA Development", "Convocatorias periódicas (febrero, septiembre)", "3-4 meses antes"],
                ["Eurimages", "Sesiones trimestrales", "60 días antes de la sesión"],
                ["Ibermedia Desarrollo / Coproducción", "Septiembre–octubre", "Julio–agosto"],
                ["ICEC Cataluña Desarrollo", "Anual (suele ser primavera)", "Invierno previo"],
                ["Junta de Andalucía Desarrollo", "Diciembre–enero", "Octubre–noviembre"],
                ["RTVE / TV3 / ETB Co-pro", "Continuo (con ventanas)", "Pitch + dossier en mano"],
              ]} />
              <p>
                Verifica siempre en el <strong>BOE</strong> (estatales) y en el boletín oficial autonómico
                correspondiente para fechas exactas del año en curso.
              </p>
            </Section>

            <Section id="activas" title="12. Convocatorias activas (búsqueda en vivo)">
              <p>
                Esta sección se actualiza <strong>en tiempo real</strong> mediante búsqueda web. Pulsa el botón
                para disparar una nueva búsqueda combinando ICAA, CCAA, programas europeos, fondos
                internacionales y laboratorios de festivales. La IA resume y deduplica los resultados.
              </p>
              <LiveCalls />
            </Section>

            <Section id="presentar" title="13. Cómo presentar el dossier según la convocatoria">
              <p>
                No todas las ventanillas piden lo mismo. Adapta el dossier al lector. Resumen práctico:
              </p>

              <h3>13.1 ICAA Selectivas (proyecto en desarrollo)</h3>
              <ul>
                <li><strong>Tono</strong>: institucional, riguroso, con énfasis en el valor cultural y el carácter español.</li>
                <li><strong>Memoria de dirección</strong> y <strong>memoria de producción</strong> en cuadernillos diferenciados (máx. 30 págs).</li>
                <li>Argumenta carácter cultural (test ICAA), trayectoria, viabilidad, impacto socioeconómico.</li>
                <li>Incluye <strong>plan de accesibilidad</strong> (subtitulado especial + audiodescripción).</li>
                <li>Incluye <strong>plan de sostenibilidad</strong> ambiental / huella de carbono.</li>
                <li>Adjunta <strong>teaser o video-pitch</strong> (suma 1 pt en valoración subjetiva).</li>
                <li>Si encajas en cuotas: <em>mujer directora, documental, animación, nuevo realizador, lengua cooficial</em>, marca claramente la línea desde la portada.</li>
              </ul>

              <h3>13.2 RTVE y TVs públicas (TV3, ETB, À Punt, CRTVG, Telemadrid…)</h3>
              <ul>
                <li><strong>Tono</strong>: vendedor, claro, orientado a programación y audiencia.</li>
                <li><strong>Logline impactante</strong> en la primera página + storyline en 1-2 párrafos.</li>
                <li><strong>Tratamiento visual</strong> con frames o moodboard (la imagen vende).</li>
                <li>Indica claramente: <em>género · duración · target · ventana de emisión · franja horaria sugerida</em>.</li>
                <li>Plan de promoción y difusión (¿qué hace que el espectador encienda la tele?).</li>
                <li>Si pides coproducción, propón <strong>contraprestación</strong> concreta (créditos, derechos territoriales, ventanas).</li>
                <li>Brevedad: 12-20 páginas máximo. La gente de programación tiene poco tiempo.</li>
              </ul>

              <h3>13.3 Eurimages</h3>
              <ul>
                <li><strong>Idioma</strong>: inglés (no aceptan castellano salvo coproducción minoritaria explicada).</li>
                <li>Acredita <strong>coproducción europea</strong> real: al menos 2 países miembros con compromisos firmados.</li>
                <li>Cuida los <strong>letters of commitment</strong> de coproductores y broadcasters.</li>
                <li><strong>Cultural significance</strong> + <strong>artistic value</strong>: aspectos clave del scoring.</li>
                <li>Memoria de dirección emocional y argumentada; evita la retórica institucional española.</li>
              </ul>

              <h3>13.4 Creative Europe MEDIA</h3>
              <ul>
                <li><strong>Idioma</strong>: inglés.</li>
                <li>Énfasis en <strong>potencial europeo</strong>: ¿por qué interesa fuera de España?</li>
                <li>Plan de circulación europea: festivales objetivo, ventas internacionales, estrategia de festival.</li>
                <li>Para <em>Slate Funding</em>: enseña la cartera completa de proyectos de la productora (slate).</li>
                <li>Trazabilidad económica de cada euro: justifica gasto, hitos y entregables.</li>
              </ul>

              <h3>13.5 Ibermedia</h3>
              <ul>
                <li><strong>Coproducción iberoamericana</strong>: al menos un país miembro coproductor.</li>
                <li>Atención al <strong>aporte iberoamericano</strong>: equipo, localizaciones, financiación.</li>
                <li>Resalta intercambio cultural y proyección continental.</li>
              </ul>

              <h3>13.6 Comunidades Autónomas (ICEC, AAIICC, AGADIC, IVC…)</h3>
              <ul>
                <li>Resalta el <strong>arraigo territorial</strong>: localizaciones, equipo local, lengua cooficial si aplica.</li>
                <li>Vincula el proyecto a la <strong>industria audiovisual de la región</strong> (productora local, empleos generados).</li>
                <li>Argumenta el <strong>impacto cultural</strong> y de marca del territorio.</li>
                <li>Si la convocatoria es de desarrollo: foco en proceso (research, escritura, scouting), no en producción.</li>
              </ul>

              <h3>13.7 Inversores privados / business angels / crowd</h3>
              <ul>
                <li><strong>Tono</strong>: financiero, ROI, exit strategy.</li>
                <li>Modeliza claramente el <strong>incentivo fiscal</strong> que recibirá el inversor (AIE, deducción 30%/25%).</li>
                <li>Cuenta el retorno potencial: ventanas, ventas internacionales, estimaciones de taquilla.</li>
                <li>Muestra trayectoria de la productora y track record similar.</li>
              </ul>

              <Tip>
                <strong>Truco práctico</strong>: mantén siempre una versión <em>master</em> del dossier
                (completa, larga) y produce derivadas más cortas / más visuales / en inglés según la
                ventanilla. La plataforma permite duplicar proyectos: úsalo para mantener variantes sin
                perder el original.
              </Tip>
            </Section>

            <Section id="enlaces" title="14. Enlaces útiles">
              <ul className="list-disc pl-5 text-sm sm:text-base">
                <li><Ext href="https://www.cultura.gob.es/cultura/areas/cine/ayudas.html">ICAA · Ayudas (Ministerio de Cultura)</Ext></li>
                <li><Ext href="https://www.cultura.gob.es/cultura/areas/cine/ayudas/produccion/selectivas.html">ICAA · Ayudas selectivas</Ext></li>
                <li><Ext href="https://www.cultura.gob.es/cultura/areas/cine/ayudas/produccion/generales.html">ICAA · Ayudas generales</Ext></li>
                <li><Ext href="https://spainaudiovisualhub.digital.gob.es/es/financiacion-y-ayudas">Spain Audiovisual Hub · Financiación</Ext></li>
                <li><Ext href="https://spainfilmcommission.com/wp-content/uploads/2025/02/MANUAL-INCENTIVOS-FISCALES.pdf">Spain Film Commission · Manual de incentivos fiscales</Ext></li>
                <li><Ext href="https://www.canaryislandsfilm.com/">Canary Islands Film Commission</Ext></li>
                <li><Ext href="https://cultura.gencat.cat/es/icec/">ICEC Cataluña</Ext></li>
                <li><Ext href="https://www.juntadeandalucia.es/organismos/aaic.html">AAIICC Andalucía</Ext></li>
                <li><Ext href="https://www.agadic.gal/">AGADIC Galicia</Ext></li>
                <li><Ext href="https://culture.ec.europa.eu/creative-europe">Creative Europe MEDIA</Ext></li>
                <li><Ext href="https://www.coe.int/en/web/eurimages">Eurimages</Ext></li>
                <li><Ext href="https://www.programaibermedia.com/">Ibermedia</Ext></li>
              </ul>
              <p className="mt-6 text-xs italic text-stone-500">
                Aviso legal: este resumen tiene fines orientativos. Las cuantías, plazos y porcentajes pueden
                variar cada año. Consulta siempre la convocatoria oficial publicada en BOE o boletín autonómico.
              </p>
            </Section>
          </article>
        </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-24" data-testid={`funding-section-${id}`}>
    <h2 className="font-display text-2xl tracking-tight text-stone-900 sm:text-3xl">{title}</h2>
    <div className="mt-4 space-y-3 font-serif text-[15px] leading-relaxed text-stone-800">
      {children}
    </div>
  </section>
);

const Tip = ({ children }) => (
  <aside className="my-3 rounded-lg border-l-4 border-[#26775E] bg-[#26775E]/5 px-4 py-3 text-[13px] text-stone-700">
    <span className="mr-2 inline-block rounded-full bg-[#26775E] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white">Tip</span>
    {children}
  </aside>
);

const Table = ({ cols, rows }) => (
  <div className="my-4 overflow-x-auto rounded-lg border border-stone-300/70">
    <table className="w-full min-w-[480px] text-sm">
      <thead>
        <tr className="bg-stone-900 text-left text-[10px] uppercase tracking-widest text-[#FAFAF6]">
          {cols.map((c, i) => <th key={i} className="px-3 py-2">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-stone-200 even:bg-[#FBFAF4]">
            {r.map((cell, j) => (
              <td key={j} className={`px-3 py-2 ${j === 0 ? "font-medium text-stone-800" : "tabular-nums text-stone-700"}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
     className="text-[#26775E] underline-offset-2 hover:underline">
    {children} ↗
  </a>
);

function LiveCalls() {
  const [data, setData] = useState(null);  // {updated_at, calls}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [settings, setSettings] = useState({ configured: false, source: null, masked: null });
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [showKeyEdit, setShowKeyEdit] = useState(false);

  const loadSettings = async () => {
    try {
      const r = await api.get("/funding/settings");
      setSettings(r.data);
    } catch (e) { console.warn("funding/settings:", e?.message); }
  };

  useEffect(() => {
    let alive = true;
    api.get("/funding/calls/cached")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    loadSettings();
    return () => { alive = false; };
  }, []);

  const saveKey = async () => {
    setSavingKey(true); setError("");
    try {
      await api.put("/funding/settings", { api_key: keyInput.trim() });
      setKeyInput("");
      setShowKeyEdit(false);
      await loadSettings();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error guardando la clave");
    } finally {
      setSavingKey(false);
    }
  };

  const refresh = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.post("/funding/calls/refresh", {}, { timeout: 240000 });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error en la búsqueda.");
    } finally {
      setLoading(false);
    }
  };

  const calls = (data?.calls || []).filter((c) => filter === "all" || c.region === filter);
  const grouped = calls.reduce((acc, c) => { (acc[c.region] ||= []).push(c); return acc; }, {});
  const REGIONS = ["Spain national", "CCAA", "Europe", "International", "Festival"];

  return (
    <div className="my-6 rounded-2xl border border-[#26775E]/30 bg-white p-4 sm:p-6" data-testid="live-calls">
      {/* Settings strip */}
      <div className="mb-4 rounded-lg border border-stone-200 bg-[#FBFAF4] p-3 text-[12px]" data-testid="tavily-settings">
        {settings.configured && !showKeyEdit && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-stone-700">
              <span className="rounded-full bg-[#26775E]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#26775E]">Conectado</span>
              {" "}Tavily {settings.source === "db" ? "(guardada en la app)" : "(variable de entorno)"} · <span className="font-mono">{settings.masked || ""}</span>
            </span>
            <button onClick={() => setShowKeyEdit(true)} data-testid="tavily-edit-btn"
                    className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-[#26775E]">
              Cambiar clave
            </button>
          </div>
        )}
        {!settings.configured && !showKeyEdit && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-stone-700">
              <span className="rounded-full bg-[#941620]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#941620]">Sin conectar</span>
              {" "}Necesitas una API key de Tavily (gratis · <a href="https://app.tavily.com" target="_blank" rel="noopener noreferrer" className="underline">app.tavily.com</a>).
            </span>
            <button onClick={() => setShowKeyEdit(true)} data-testid="tavily-connect-btn"
                    className="rounded-full bg-[#26775E] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]">
              Conectar Tavily
            </button>
          </div>
        )}
        {showKeyEdit && (
          <div className="space-y-2">
            <label className="block text-[10px] font-semibold uppercase tracking-widest text-stone-600">
              Pega tu API key de Tavily (empieza por <span className="font-mono">tvly-</span>)
            </label>
            <input value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
                   placeholder="tvly-..."
                   data-testid="tavily-key-input"
                   className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 font-mono text-[12px] outline-none focus:border-[#26775E]" />
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={saveKey} disabled={savingKey || !keyInput.trim()} data-testid="tavily-save-btn"
                      className="rounded-full bg-[#26775E] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
                {savingKey ? "Guardando…" : "Guardar"}
              </button>
              <button onClick={() => { setShowKeyEdit(false); setKeyInput(""); }} data-testid="tavily-cancel-btn"
                      className="rounded-full px-4 py-1.5 text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900">
                Cancelar
              </button>
              {settings.configured && (
                <button onClick={async () => { setKeyInput(""); await saveKey(); }} data-testid="tavily-disconnect-btn"
                        className="ml-auto rounded-full border border-red-300 px-4 py-1.5 text-[10px] uppercase tracking-widest text-red-600 hover:bg-red-50">
                  Desconectar
                </button>
              )}
            </div>
            <p className="text-[11px] text-stone-500">
              Se guarda en la base de datos de la plataforma. Solo el equipo autorizado puede verla y modificarla.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">Búsqueda en vivo · Tavily + Claude</p>
          <p className="mt-1 text-sm text-stone-600">
            {data?.updated_at
              ? <>Última actualización: <strong>{new Date(data.updated_at).toLocaleString("es-ES")}</strong></>
              : "Aún no se ha buscado nada. Conecta Tavily y pulsa el botón."}
          </p>
        </div>
        <button onClick={refresh} disabled={loading || !settings.configured} data-testid="funding-refresh-btn"
                className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48] disabled:opacity-50">
          {loading ? "Buscando…" : "Actualizar convocatorias"}
        </button>
      </div>

      {error && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="funding-error">{error}</p>}

      {data?.calls?.length > 0 && (
        <>
          <div className="mt-4 flex flex-wrap gap-1">
            <FilterPill label="Todas" active={filter === "all"} onClick={() => setFilter("all")} />
            {REGIONS.map((r) => (
              <FilterPill key={r} label={r} active={filter === r} onClick={() => setFilter(r)} />
            ))}
          </div>

          <div className="mt-4 space-y-5">
            {REGIONS.map((r) => {
              const list = grouped[r] || [];
              if (!list.length) return null;
              return (
                <div key={r} data-testid={`group-${r}`}>
                  <h4 className="font-display text-lg text-stone-900">{r} <span className="text-xs text-stone-400">· {list.length}</span></h4>
                  <ul className="mt-2 space-y-2">
                    {list.map((c, i) => {
                      const key = `${c.source_url || ""}|${c.title || ""}|${i}`;
                      return (
                      <li key={key} className="rounded-lg border border-stone-200 bg-[#FBFAF4] p-3" data-testid={`call-${i}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                             className="text-sm font-medium text-stone-900 hover:text-[#26775E]">
                            {c.title} ↗
                          </a>
                          <StatusBadge status={c.status} />
                        </div>
                        <p className="mt-1 text-[13px] leading-snug text-stone-700">{c.summary}</p>
                        <p className="mt-1 text-[11px] text-stone-500">
                          {c.deadline ? <>Cierre: <strong>{c.deadline}</strong></> : <em>Sin fecha conocida</em>}
                        </p>
                      </li>);
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}

      {data && data.calls && data.calls.length === 0 && !loading && (
        <p className="mt-4 text-sm italic text-stone-500">No se han encontrado convocatorias. Prueba a actualizar.</p>
      )}
    </div>
  );
}

const FilterPill = ({ label, active, onClick }) => (
  <button onClick={onClick}
          className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest transition ${
            active ? "bg-stone-900 text-[#FAFAF6]" : "bg-stone-200 text-stone-700 hover:bg-stone-300"
          }`}>{label}</button>
);

const StatusBadge = ({ status }) => {
  const cfg = {
    open: { label: "Abierta", cls: "bg-[#26775E]/15 text-[#26775E]" },
    closed: { label: "Cerrada", cls: "bg-stone-200 text-stone-600" },
    unknown: { label: "Por confirmar", cls: "bg-[#941620]/15 text-[#941620]" },
  }[status] || { label: status, cls: "bg-stone-100 text-stone-600" };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${cfg.cls}`}>{cfg.label}</span>;
};
